import { Box } from '@mui/material';
import { Button } from '@malash-office/ui-components';
import { ChatAction } from '../hooks/useAgentChat';

interface ChatActionCardProps {
  action: ChatAction;
  onConfirm: (id: string) => void;
  onReject: (id: string) => void;
}

/**
 * The confirmation gate, as the user sees it.
 *
 * Everything the agent wants to change passes through here. It shows the described effect *and*
 * the arguments verbatim rather than a tidied summary: "update the price" and "update the price to
 * 4" are different approvals, and only the second is one a person can actually give.
 */
const STATUS_LABEL: Record<ChatAction['status'], string> = {
  pending: '',
  executing: 'מבצע…',
  done: 'בוצע',
  failed: 'נכשל',
  rejected: 'נדחה'
};

const STATUS_COLOR: Record<ChatAction['status'], string> = {
  pending: 'warning.main',
  executing: 'info.main',
  done: 'success.main',
  failed: 'error.main',
  rejected: 'text.disabled'
};

export const ChatActionCard = ({ action, onConfirm, onReject }: ChatActionCardProps) => {
  const isPending = action.status === 'pending';

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: STATUS_COLOR[action.status],
        borderRadius: '14px',
        p: 1.5,
        backgroundColor: 'grey.50',
        opacity: action.status === 'rejected' ? 0.6 : 1
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
          פעולה מבוקשת — נדרש אישורך
        </Box>
        {STATUS_LABEL[action.status] && (
          <Box sx={{ fontSize: 12, fontWeight: 600, color: STATUS_COLOR[action.status], whiteSpace: 'nowrap' }}>
            {STATUS_LABEL[action.status]}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 0.75, fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{action.summary}</Box>

      {Object.keys(action.args).length > 0 && (
        <Box
          component="pre"
          sx={{
            mt: 1,
            mb: 0,
            p: 1,
            borderRadius: '8px',
            backgroundColor: 'common.white',
            border: '1px solid',
            borderColor: 'grey.200',
            fontSize: 11,
            lineHeight: 1.6,
            overflowX: 'auto',
            // The payload is JSON with Latin keys; RTL would scramble it.
            direction: 'ltr',
            textAlign: 'left'
          }}
        >
          {JSON.stringify(action.args, null, 2)}
        </Box>
      )}

      {action.error && (
        <Box sx={{ mt: 1, fontSize: 12, color: 'error.main' }}>{action.error}</Box>
      )}

      {isPending && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button size="small" onClick={() => onConfirm(action.id)}>
            אישור וביצוע
          </Button>
          <Button size="small" variant="outlined" onClick={() => onReject(action.id)}>
            ביטול
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ChatActionCard;
